import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Button, Fab, SvgIcon, Tooltip } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';

/**
 * Brand-aware WhatsApp CTAs that funnel visitors to the AfroEspace
 * community group. Two surfaces share this file:
 *
 *   • {@link CommunityJoinButton} — green pill button intended for the
 *     footer (or a marketing section). High-contrast WhatsApp green so
 *     it reads as a primary CTA rather than a generic link.
 *
 *   • {@link FloatingWhatsAppButton} — fixed circle bottom-right, the
 *     "always-there" entry point. Self-hides when the env var isn't
 *     configured so a half-set staging deploy doesn't dangle a broken
 *     icon over the UI.
 *
 * The invite URL is sourced from {@code VITE_COMMUNITY_WHATSAPP_URL}.
 * Leaving the env empty hides BOTH surfaces — single source of truth.
 */
const WHATSAPP_GREEN = '#25D366';
const WHATSAPP_GREEN_HOVER = '#1EBE5D';

function getInviteUrl(): string | null {
  const raw = (import.meta.env.VITE_COMMUNITY_WHATSAPP_URL as string | undefined)?.trim();
  return raw && raw.length > 0 ? raw : null;
}

/** Brand WhatsApp glyph — no first-party MUI icon, inline SVG. */
function WhatsAppIcon(props: React.ComponentProps<typeof SvgIcon>) {
  return (
    <SvgIcon viewBox="0 0 24 24" {...props}>
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.93.51 3.79 1.47 5.43L2 22l4.78-1.26a9.84 9.84 0 0 0 5.26 1.51h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.83 9.83 0 0 0 12.04 2zm0 18.15a8.22 8.22 0 0 1-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.18 8.18 0 0 1-1.25-4.38c0-4.53 3.69-8.22 8.23-8.22 2.2 0 4.26.86 5.81 2.41a8.16 8.16 0 0 1 2.41 5.83c0 4.54-3.69 8.22-8.22 8.22zm4.52-6.16c-.25-.13-1.47-.72-1.69-.81-.23-.08-.39-.13-.56.13-.16.25-.64.81-.78.97-.14.16-.28.18-.53.06-.25-.13-1.06-.39-2.01-1.24a7.55 7.55 0 0 1-1.4-1.73c-.15-.25-.02-.39.11-.51.11-.11.25-.28.37-.42.13-.14.16-.25.25-.41.08-.16.04-.31-.02-.43-.06-.13-.56-1.34-.76-1.84-.2-.49-.41-.42-.56-.43h-.48a.92.92 0 0 0-.67.31c-.23.25-.88.86-.88 2.1 0 1.24.9 2.44 1.03 2.6.13.16 1.77 2.71 4.29 3.8.6.26 1.07.41 1.43.53.6.19 1.15.16 1.59.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.08.14-1.18-.06-.1-.23-.16-.48-.29z"/>
    </SvgIcon>
  );
}

/**
 * Footer-style "Rejoindre la communauté" pill — full-width on mobile,
 * auto-width on desktop. Renders nothing when no invite URL is set.
 */
export function CommunityJoinButton({ sx }: { sx?: SxProps<Theme> }) {
  const { t } = useTranslation();
  const url = getInviteUrl();
  if (!url) return null;

  return (
    <Button
      component="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      startIcon={<WhatsAppIcon />}
      sx={{
        bgcolor: WHATSAPP_GREEN,
        color: 'common.white',
        fontWeight: 700,
        textTransform: 'none',
        px: 2.5,
        py: 1.25,
        borderRadius: 999,
        boxShadow: 'none',
        '&:hover': {
          bgcolor: WHATSAPP_GREEN_HOVER,
          boxShadow: 'none',
        },
        ...sx,
      }}
    >
      {t('community.joinCta')}
    </Button>
  );
}

/**
 * Fixed-position WhatsApp circle, lower-right. Sits below the PWA
 * install banner (zIndex 1300) so when both show the install banner
 * stays on top without occluding the WhatsApp button entirely — the
 * PWA banner is dismissable, this button is permanent.
 */
export function FloatingWhatsAppButton() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const url = getInviteUrl();
  if (!url) return null;
  // Hide on the chat page — the FAB overlaps the message input/send button
  // at bottom-right and visually blocks typing.
  if (pathname.startsWith('/messages/')) return null;

  return (
    <Tooltip title={t('community.joinCta')} placement="left" arrow>
      <Fab
        component="a"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t('community.joinCta')}
        sx={{
          position: 'fixed',
          right: { xs: 16, sm: 24 },
          bottom: { xs: 16, sm: 24 },
          bgcolor: WHATSAPP_GREEN,
          color: 'common.white',
          zIndex: 1200,
          boxShadow: '0 8px 24px rgba(37, 211, 102, 0.45)',
          '&:hover': {
            bgcolor: WHATSAPP_GREEN_HOVER,
            boxShadow: '0 10px 28px rgba(37, 211, 102, 0.55)',
          },
        }}
      >
        <WhatsAppIcon sx={{ fontSize: '1.6rem' }} />
      </Fab>
    </Tooltip>
  );
}
